"use client";

import {
  readTgWebAppStartParamFromLocation,
  resolveTelegramMiniAppEffectiveStartParam,
  resolveTelegramStartParamRedirect,
  telegramStartParamTargetMatches,
} from "@calcom/features/auth/lib/verifyTelegramWebAppInitData";
import { useSession } from "next-auth/react";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";

const CONSUMED_KEY = "knopka_tg_start_param_consumed";

type TelegramWebApp = {
  initData?: string;
  initDataUnsafe?: {
    start_param?: string;
    user?: { id?: number | string };
  };
};

function getTelegramWebApp(): TelegramWebApp | null {
  if (typeof window === "undefined") return null;
  return (window as Window & { Telegram?: { WebApp?: TelegramWebApp } }).Telegram?.WebApp || null;
}

function isTelegramWebAppContext(): boolean {
  const tg = getTelegramWebApp();
  if (!tg) return false;
  return Boolean(tg.initData || tg.initDataUnsafe?.user || tg.initDataUnsafe?.start_param);
}

function readEffectiveStartParam(): string | undefined {
  const tg = getTelegramWebApp();
  const fromInit = tg?.initDataUnsafe?.start_param?.trim() || undefined;
  const fromLaunch =
    typeof window !== "undefined"
      ? readTgWebAppStartParamFromLocation(window.location.search, window.location.hash)
      : undefined;
  return resolveTelegramMiniAppEffectiveStartParam(fromInit, null, fromLaunch);
}

function isTelegramAuthPath(pathname: string | null): boolean {
  if (!pathname) return false;
  return (
    pathname.startsWith("/auth/telegram/mini") ||
    pathname.startsWith("/auth/telegram/invite") ||
    pathname.startsWith("/auth/telegram/link") ||
    pathname.startsWith("/auth/telegram/email") ||
    pathname.startsWith("/auth/telegram")
  );
}

function readConsumed(): string | null {
  try {
    return sessionStorage.getItem(CONSUMED_KEY);
  } catch {
    return null;
  }
}

function writeConsumed(value: string): void {
  try {
    sessionStorage.setItem(CONSUMED_KEY, value);
  } catch {
    // private mode / blocked storage — non-fatal
  }
}

/**
 * Warm Mini App WebViews often stay on /bookings/upcoming when a new
 * t.me/.../calendar?startapp=… link is opened. Re-read start_param and redirect.
 */
export function KnopkaTelegramStartParamGuard() {
  const pathname = usePathname();
  const router = useRouter();
  const { status } = useSession();
  const redirecting = useRef(false);

  const tryRedirect = useCallback(() => {
    if (typeof window === "undefined") return;
    if (!isTelegramWebAppContext()) return;
    if (isTelegramAuthPath(pathname)) return;
    if (redirecting.current) return;

    const startParam = readEffectiveStartParam();
    if (!startParam) return;

    const target = resolveTelegramStartParamRedirect(startParam);
    if (!target) return;

    const search = window.location.search || "";
    if (telegramStartParamTargetMatches(pathname, search, target)) {
      writeConsumed(startParam);
      return;
    }

    // Same start_param already handled this session — allow free navigation
    // (e.g. user opened deep link, then went to /bookings/upcoming).
    // New value (different deep link) clears the skip.
    if (readConsumed() === startParam) return;

    redirecting.current = true;
    writeConsumed(startParam);

    if (status === "authenticated") {
      router.replace(target);
      return;
    }

    if (status === "unauthenticated") {
      router.replace("/auth/telegram/mini");
      return;
    }

    // session still loading — retry on next status change
    redirecting.current = false;
    try {
      sessionStorage.removeItem(CONSUMED_KEY);
    } catch {
      // ignore
    }
  }, [pathname, router, status]);

  useEffect(() => {
    tryRedirect();
  }, [tryRedirect]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      redirecting.current = false;
      // Deep link on a warm WebView usually just resumes the app — allow the same
      // start_param to redirect again (user tapped the link while already in Mini App).
      try {
        sessionStorage.removeItem(CONSUMED_KEY);
      } catch {
        // ignore
      }
      tryRedirect();
    };

    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [tryRedirect]);

  return null;
}
